import { usePersistedState } from "@/lib/persistedFilters";
// The working area for one report: parameters → generate / download / email / schedule.
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarClock, Download, FileText, Loader2, Mail, Play, Upload, Lock, Globe, HardDriveUpload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useTablePagination, TablePagination, PageSizeSelect } from "@/components/TablePagination";
import { ParamFields } from "./ParamFields";
import { DeliverDialog } from "./DeliverDialog";
import { ScheduleDialog } from "./ScheduleDialog";
import { defaultParams, downloadReport, missingRequired, runReport } from "./reportsApi";

function fmtCell(v, money = false) {
  if (v === null || v === undefined) return "";
  if (money && typeof v === "number") return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (typeof v === "number") return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return String(v);
}

function CsvUploadCard({ csvKey, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${csvKey}/upload?mode=replace`, fd);
      toast.success(`Replaced with ${data.row_count} rows`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-3 py-2 text-xs text-slate-600" data-testid="report-csv-upload">
      <Upload className="h-3.5 w-3.5 text-primary" />
      <span>This report is fed by a CSV. First row is the header; the upload replaces the current data.</span>
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="report-csv-input" />
      <Button size="sm" variant="outline" className="ml-auto h-7" disabled={busy} onClick={() => ref.current?.click()} data-testid="report-csv-choose">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Upload CSV"}
      </Button>
    </div>
  );
}

export function ReportWorkbench({ report, allData, onScheduled }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  // Iter 309 — a drill-through link may prefill params (?frm=…&domain=…) and auto-run.
  const [sp] = useSearchParams();
  const withUrlParams = (base) => {
    const next = { ...base };
    let hit = false;
    (report.params || []).forEach((p) => { const v = sp.get(p.key); if (v !== null) { next[p.key] = v; hit = true; } });
    return [next, hit];
  };
  const [params, setParams] = useState(() => withUrlParams(defaultParams(report))[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [q, setQ] = usePersistedState(`report-rows.${report.key}.q`, "");
  const [dialog, setDialog] = useState(null);   // "deliver" | "schedule"
  const autoRun = useRef(false);

  useEffect(() => {
    const [next, hit] = withUrlParams(defaultParams(report));
    setParams(next); setData(null);
    autoRun.current = hit;
  }, [report.key]);   // eslint-disable-line react-hooks/exhaustive-deps

  const missing = missingRequired(report, params);

  useEffect(() => {
    if (autoRun.current && !missing.length) { autoRun.current = false; generate(); }
  }, [params]);   // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (missing.length) { toast.error(`Fill in: ${missing.map((p) => p.label).join(", ")}`); return; }
    setLoading(true);
    try {
      const { data: d } = await runReport(report.key, params);
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "The report could not be generated");
    } finally { setLoading(false); }
  };

  const download = async (fmt) => {
    if (missing.length) { toast.error(`Fill in: ${missing.map((p) => p.label).join(", ")}`); return; }
    setDownloading(fmt);
    try {
      const res = await downloadReport(report.key, params, fmt);
      const cd = res.headers["content-disposition"] || "";
      downloadBlob(res.data, (cd.match(/filename="?([^";]+)/) || [])[1] || `${report.key}.${fmt}`);
    } catch (e) {
      let msg = "Download failed";
      try { msg = formatApiErrorDetail(JSON.parse(await e.response?.data?.text?.())?.detail) || msg; } catch { /* blob error */ }
      toast.error(msg);
    } finally { setDownloading(""); }
  };

  const rows = useMemo(() => data?.rows || [], [data]);
  const cols = useMemo(() => data?.columns || [], [data]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => cols.some((c) => String(r[c.key] ?? "").toLowerCase().includes(s)));
  }, [rows, cols, q]);
  const pg = useTablePagination(filtered, 25);

  return (
    <div className="space-y-4" data-testid={`report-workbench-${report.key}`}>
      <Card className="border-slate-200 p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{report.group}</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100" data-testid="report-title">{report.label}</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">{report.description}</p>
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            !report.scoped || allData ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                                       : "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"}`}
                data-testid="report-scope-pill">
            {!report.scoped ? <><Globe className="h-3 w-3" /> Platform-wide</>
              : allData ? <><Globe className="h-3 w-3" /> All data</> : <><Lock className="h-3 w-3" /> Your data only</>}
          </span>
        </div>

        <div className="mt-5">
          <ParamFields report={report} params={params} onChange={setParams} />
        </div>

        {report.csv_key && isSuper && (
          <div className="mt-4"><CsvUploadCard csvKey={report.csv_key} onDone={generate} /></div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {report.formats.includes("inline") && (
            <Button onClick={generate} disabled={loading} data-testid="report-generate">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Generate
            </Button>
          )}
          {report.formats.includes("csv") && (
            <Button variant="outline" onClick={() => download("csv")} disabled={!!downloading} data-testid="report-download-csv">
              {downloading === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} CSV
            </Button>
          )}
          {report.formats.includes("pdf") && (
            <Button variant="outline" onClick={() => download("pdf")} disabled={!!downloading} data-testid="report-download-pdf">
              {downloading === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
            </Button>
          )}
          <span className="mx-1 hidden h-6 w-px bg-slate-200 sm:block" />
          <Button variant="outline" onClick={() => setDialog("deliver")} data-testid="report-email-now">
            <Mail className="h-4 w-4" /> Email now
          </Button>
          {(report.formats.includes("csv") || report.formats.includes("pdf")) && (
            <Button variant="outline" onClick={() => setDialog("sftp")} data-testid="report-sftp-now">
              <HardDriveUpload className="h-4 w-4" /> Send to SFTP
            </Button>
          )}
          <Button variant="outline" onClick={() => setDialog("schedule")} data-testid="report-schedule">
            <CalendarClock className="h-4 w-4" /> Schedule
          </Button>
        </div>
      </Card>

      {data && (
        <Card className="border-slate-200 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid="report-result">
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <div className="flex flex-wrap gap-2" data-testid="report-summary">
              {(data.summary || []).map((s) => (
                <span key={s.label} className="inline-flex items-baseline gap-1.5 rounded-md bg-slate-50 px-2.5 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  <span className="font-semibold uppercase tracking-wide text-[10px] text-slate-400">{s.label}</span>
                  <span className="font-heading text-sm font-semibold text-slate-900 dark:text-slate-100">{typeof s.value === "number" ? s.value.toLocaleString() : s.value}</span>
                </span>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Input className="h-8 w-48" placeholder="Filter rows…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="report-filter" />
              <span className="text-xs text-slate-500" data-testid="report-row-count">
                {data.row_count.toLocaleString()} rows{data.row_count > rows.length ? ` · showing first ${rows.length}` : ""}
              </span>
            </div>
          </div>
          {data.subtitle && <div className="px-4 pt-3 text-xs text-slate-500" data-testid="report-subtitle">{data.subtitle}</div>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>{cols.map((c) => <th key={c.key} className={`px-4 py-2 ${c.numeric ? "text-right" : ""}`}>{c.label}</th>)}</tr>
              </thead>
              <tbody data-testid="report-rows">
                {pg.pageItems.map((r, i) => (
                  <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/50 dark:border-slate-800 dark:hover:bg-slate-800/40" data-testid={`report-row-${i}`}>
                    {cols.map((c) => (
                      <td key={c.key} className={`max-w-[320px] truncate px-4 py-2 text-slate-700 dark:text-slate-200 ${c.numeric ? "text-right font-mono tabular-nums" : ""}`}
                          title={fmtCell(r[c.key], c.money)}>{fmtCell(r[c.key], c.money)}</td>
                    ))}
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={Math.max(cols.length, 1)} className="px-4 py-10 text-center text-slate-400" data-testid="report-no-rows">No rows to show</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {filtered.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-2 dark:border-slate-800">
              <PageSizeSelect pageSize={pg.pageSize} setPageSize={pg.setPageSize} testid="report-page-size" />
              <TablePagination {...pg} testid="report-pagination" />
            </div>
          )}
        </Card>
      )}

      {dialog === "deliver" && <DeliverDialog report={report} params={params} open onClose={() => setDialog(null)} />}
      {dialog === "sftp" && <DeliverDialog report={report} params={params} open channel="sftp" onClose={() => setDialog(null)} />}
      {dialog === "schedule" && (
        <ScheduleDialog report={report} params={params} open onClose={(saved) => { setDialog(null); if (saved) onScheduled?.(saved); }} />
      )}
    </div>
  );
}
